<script setup lang="ts">
import { mdiArrowLeft } from '@mdi/js'

interface Props {
  sceneId: string
  hideBackButton?: boolean
  backToText?: string
  /**
   * #719 観点B: モバイルのリスト型ナビへ切り替える。ページ側が useIsMobile で
   * 判定し、レイアウト（mobileNav ⇔ centering43）と一緒に渡す。未指定（false）の
   * ページは従来どおりデスクトップ SVG マップのみ描画（挙動不変）。
   */
  mobile?: boolean
  /**
   * #719 観点B: モバイルナビの背景に薄暗く敷くそのエリアのマップ画像 URL。
   * 「ここがどこか」を一目で伝える（渦巻き模様の余白埋めは使わない）。
   * ページ側が `import bg from '@/assets/...'` で解決した URL を渡す。
   */
  mobileBackground?: string
  /**
   * #719 観点B: モバイルナビに並べる遷移先 scene-id を明示指定する。ハブ（キャンプ）
   * のように親子グラフ（listVisibleChildren）と手描き SVG の導線が一致しない画面用。
   * 未指定なら listVisibleChildren(sceneId)（world / エリアはこれで SVG と一致する）。
   */
  mobileSceneIds?: string[]
}

const props = defineProps<Props>()
const emit = defineEmits(['clickScene', 'clickBack', 'loaded'])

const gameStateStore = useGameStateStore()
const { isLoaded } = storeToRefs(gameStateStore)
const { getSceneInfo: getSceneById, reloadGameState } = gameStateStore
const { triggerEventAndProcess, processEventsSequentially } = useEventSystem()
const { trackAreaVisit, trackGiftRedeem } = useFunnelEvent()
const api = useApi()
const dialogStore = useDialogStore()

useMusicOnMount(props.sceneId)

// 1. Setup event listeners for the scene map
onMounted(async (): Promise<void> => {
  // console.log(currentScene.value)
  if (!isLoaded.value) {
    await reloadGameState()
  }

  setupSceneListeners()

  emit('loaded')

  // wld_* (エリア) のみ area_visit を計測。cmp_* (cradle/arcaneLab/emporium) は除外。
  if (props.sceneId.startsWith('wld_')) {
    trackAreaVisit(props.sceneId)
  }

  await triggerEventAndProcess(`ENTER:${props.sceneId}`)
})

// 2. Define cleanup procedures.
onBeforeRouteLeave(async () => {
  await triggerEventAndProcess(`LEAVE:${props.sceneId}`)
})

// Event Handlers
const currentScene = computed(() => getSceneById(props.sceneId))
const { $sound } = useNuxtApp()
const { showTooltip, hideTooltip, visible, text, x, y } = useFloatingTooltip()
const { isSceneCleared, isSceneVisible, listVisibleChildren } = useSceneAccess()

// #719 観点B: モバイルのリスト型ナビに出すシーン群。mobileSceneIds が渡されれば
// その順で（可視なものだけ）、無ければ可視な子シーン（listVisibleChildren）。
const mobileChildren = computed(() => {
  if (props.mobileSceneIds?.length) {
    return props.mobileSceneIds
      .filter(id => isSceneVisible(id))
      .map(id => getSceneById(id))
  }
  return listVisibleChildren(props.sceneId)
})

// モバイルの戻るボタンは「← 行き先名」の形（矢印で "戻る" を表すので "…に戻る" は削る）。
// 例: "冒険者のキャンプに戻る" → "冒険者のキャンプ" / "ワールドマップに戻る" → "ワールドマップ"。
const mobileBackLabel = computed(() =>
  (props.backToText || 'ワールドマップに戻る').replace(/(に|へ)?戻る$/, '').trim(),
)

const onClickScene = async (sceneId: string): Promise<void> => {
  console.log(`Select ${sceneId} on ${props.sceneId}`)
  try {
    const promise = $sound.playSound('click')

    await triggerEventAndProcess(`CLICK:${sceneId}`)

    return promise
  }
  finally {
    emit('clickScene', sceneId)
  }
}

const onClickBack = async (): Promise<void> => {
  console.log(`Back from ${props.sceneId}`)
  try {
    await $sound.playSound('cancel', { wait: true })

    return triggerEventAndProcess(`CLICK:${props.sceneId}.toLeave`)
  }
  finally {
    emit('clickBack')
  }
}

const onBackFromBroker = async (): Promise<void> => {
  await triggerEventAndProcess(`${props.sceneId}.enter`)
}

const onKeySubmit = async (key: string): Promise<void> => {
  const dialog = dialogStore.key
  let response
  if (dialog.keyMode === 'gift') {
    // #721: 交易所は desktop/mobile とも SceneMap 内にネストされる（emporium.vue の
    // #map / #mobile 両スロット）。ギフトキー処理はこの SceneMap の KeyInputDialog に
    // 一本化した（ProductShelf 側の重複 KeyInputDialog は撤去）。付与種別はサーバ応答
    // からの判定が難しいため unknown で計測し、MP/切手のカウント更新のため game state
    // を再読み込みする。
    response = await api.redeemGiftKey(key)
    trackGiftRedeem('unknown')
    await reloadGameState()
  }
  else {
    const currentScene = getSceneById(dialog.sceneId!)
    response = await api.tryUnlockScene(currentScene.sceneId, key)
  }
  await processEventsSequentially(response.results)
}

// Utility functions.
const setupSceneListeners = async () => {
  for (const path of document.getElementsByClassName('scene')) {
    const sceneId = path.getAttribute('data-scene-id')
    // console.log("data-scene-id", path, sceneId)
    if (sceneId) {
      const scene = getSceneById(sceneId)
      if (scene.isVisible) {
        path.addEventListener('click', () => {
          onClickScene(sceneId)
        })
        path.addEventListener('mouseenter', (event) => {
          const scene = getSceneById(sceneId)
          const text = isSceneCleared(sceneId) ? `${scene.name}🪄` : scene.name
          showTooltip(text, event as MouseEvent)
        })
        path.addEventListener('mouseleave', () => {
          hideTooltip()
        })
      }
      else {
        path.classList.add('d-none')
      }
    }
  }
}
</script>

<template>
  <!-- デスクトップ: 従来の SVG マップ -->
  <v-main
    v-if="!mobile"
    id="scenemap-main"
  >
    <div class="map-container">
      <v-sheet class="title-banner">
        <!-- currentScene は client-side のゲーム state から復元されるため、SSR では
             scenes 未ロードで dummySceneInfo（羽根筆のゆりかご）にフォールバックし、
             CSR では正しい scene 名になる。この差が hydration text mismatch を招くので
             UserInfoCard と同様に ClientOnly で SSR から外す（mount 後に実名が入る）。 -->
        <h1><ClientOnly>{{ currentScene?.name }}</ClientOnly></h1>
      </v-sheet>

      <template v-if="!hideBackButton">
        <v-btn
          class="back-button"
          size="x-large"
          variant="outlined"
          @click="onClickBack"
        >
          {{ backToText || "ワールドマップに戻る" }}
        </v-btn>
      </template>

      <UserInfoCard class="user-info" />

      <slot name="map">
        読み込み中
      </slot>
    </div>
  </v-main>

  <!-- モバイル (#719 観点B): リスト型ナビ。ページが #mobile を渡せばそれを、
       無ければ可視な子シーンの自動リストを描画する（camp / world / エリア）。 -->
  <div
    v-else
    class="scenemap-mobile"
  >
    <div
      class="scenemap-mobile__bg"
      aria-hidden="true"
    >
      <!-- 動かしたい画面（world 等）はページが #mobile-background でレイヤを組む。
           省略時は mobileBackground の単一画像を敷く（camp / エリア）。 -->
      <slot name="mobile-background">
        <div
          v-if="mobileBackground"
          class="scenemap-mobile__bg-image"
          :style="{ backgroundImage: `url(${mobileBackground})` }"
        />
      </slot>
    </div>

    <header class="scenemap-mobile__header">
      <!-- 1段目: 戻る（左）／ユーザーカード（右） -->
      <div class="scenemap-mobile__topbar">
        <v-btn
          v-if="!hideBackButton"
          class="scenemap-mobile__back"
          variant="text"
          size="small"
          :prepend-icon="mdiArrowLeft"
          @click="onClickBack"
        >
          {{ mobileBackLabel }}
        </v-btn>
        <v-spacer />
        <!-- #719: モバイルは compact（名前を出さず大きめアバターだけ）。狭幅で名前が
             潰れて読めないより、視認できるアバターの方が情報になる。 -->
        <UserInfoCard
          compact
          class="scenemap-mobile__userinfo"
        />
      </div>
      <!-- 2段目: 地名（デスクトップ同様、SSR フォールバックとの hydration mismatch を
           避けるため ClientOnly で client 描画に寄せる） -->
      <h1 class="scenemap-mobile__title">
        <ClientOnly>{{ currentScene?.name }}</ClientOnly>
      </h1>
    </header>

    <div class="scenemap-mobile__body">
      <slot name="mobile">
        <MobileSceneNav
          :items="mobileChildren"
          @click-scene="onClickScene"
        />
      </slot>
    </div>
  </div>

  <!-- ダイアログ / ツールチップは device 非依存で両分岐で共有 -->
  <DialogEventDialog />
  <DialogNameChangeDialog />
  <DialogFairyBrokerDialog @click-back="onBackFromBroker" />
  <DialogKeyInputDialog @submit="onKeySubmit" />
  <DialogVideoDialog />
  <FloatingTooltip
    :visible="visible"
    :text="text"
    :x="x"
    :y="y"
  />
</template>

<style>
#scenemap-main {
  font-family: "Utsukushi", sans-serif;
  height: 100%;
}

#scenemap-main .map-container {
  max-width: 100%;
  height: 100%;
  position: relative;
}

#scenemap-main .map-container .title-banner {
  position: absolute;
  width: 50%;
  left: 25%;
  top: 10px;
  text-align: center;
  z-index: 3;
  color: white;
  text-shadow: #000 3px 3px 3px;
  background-color: rgba(128, 0, 32, 0.2);
}

#scenemap-main .map-container .title-banner h1 {
  font-size: max(17.78px, calc(2.5 * var(--u)));
}

#scenemap-main .map-container .back-button {
  position: absolute;
  left: 1.5%;
  /* left: clamp(13.89px, 1.95dvw, 25px); */
  /* top: 92.5%; */
  top: 92.5%;
  height: max(28.89px, calc(4.06 * var(--u)));
  /* top: clamp(648.89px, 91.25dvw, 1168px); */

  /* font-size: 1.6rem; */
  font-size: max(13.89px, calc(1.95 * var(--u)));
  font-weight: bold;

  background-color: rgba(255, 255, 255, 0.6);
  z-index: 3;
}

#scenemap-main .map-container .user-info {
  position: absolute;
  /* width: 18%; */
  /* left: 80%; */
  /* left: 1355px; */
  /* left: clamp(752.78px, 105.86dvw, 1355px); */
  right: 1.5%;
  left: auto;
  top: 1%;

  z-index: 3;
}

#scenemap-main .map-container > img {
  display: block;
  /* 画像がデフォルトのインライン要素からブロック要素に変更され、下に配置されるSVGとの間に不必要な空白がなくなります */
  width: 100%;
  height: auto;
  position: relative;
  z-index: 1;
  /* JPEG画像をSVGより背面に配置 */
}

#scenemap-main .map-container svg {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  /* z-index: 2 !important; */

  user-select: none;
}

#scenemap-main .map-container svg path.scene {
  fill: transparent;
  stroke: #000;
  stroke-width: 0px;
}

#scenemap-main .map-container svg path.scene:hover {
  cursor: pointer;
  fill: orange;
  fill-opacity: 0.3;
}

/* #719 観点B: モバイルのリスト型ナビ。mobileNav レイアウト（dvw/dvh 可）内に入る。 */
.scenemap-mobile {
  position: relative;
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  min-height: 0;
  font-family: "Noto Sans JP", sans-serif;
}

/* そのエリアのマップ画像を薄暗く全面に敷く。上部（ヘッダー／カード）は濃いめの
   スクリムで可読性を確保し、カードが尽きた下部はマップが透けて「ここがどこか」
   が伝わる。渦巻き模様（余白埋め）は使わない。 */
.scenemap-mobile__bg {
  position: absolute;
  inset: 0;
  z-index: 0;
  overflow: hidden;
}

/* 単一画像フォールバック（スロット未使用時） */
.scenemap-mobile__bg-image {
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center top;
  background-repeat: no-repeat;
}

/* 可読性用スクリム。スロットで組んだレイヤの上に重ねる */
.scenemap-mobile__bg::after {
  content: "";
  position: absolute;
  inset: 0;
  z-index: 1;
  background: linear-gradient(
    to bottom,
    rgba(24, 15, 8, 0.74) 0%,
    rgba(24, 15, 8, 0.5) 34%,
    rgba(24, 15, 8, 0.28) 100%
  );
}

.scenemap-mobile__header,
.scenemap-mobile__userinfo,
.scenemap-mobile__body {
  position: relative;
  z-index: 1;
}

.scenemap-mobile__header {
  display: flex;
  flex-direction: column;
  gap: 6px;
  /* map は full-bleed（safe-area 下まで）なので、コンテンツ側で notch を避ける */
  padding: calc(8px + env(safe-area-inset-top)) 12px 6px;
}

.scenemap-mobile__topbar {
  display: flex;
  align-items: center;
  gap: 8px;
}

.scenemap-mobile__back {
  flex: 0 0 auto;
  color: #fff;
  background: rgba(128, 0, 32, 0.55);
}

.scenemap-mobile__userinfo {
  flex: 0 0 auto;
}

.scenemap-mobile__title {
  margin: 0;
  font-size: 1.5rem;
  font-weight: 700;
  color: #fff;
  text-align: center;
  text-shadow: #000 2px 2px 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scenemap-mobile__body {
  flex: 1 1 auto;
  min-height: 0;
  /* #721: 子（MobileSceneNav / #mobile スロット）が flex:1 で高さいっぱいに広がり、
     自前で縦方向の配置・スクロールを管理できるようにする。ナビ系は上詰め（縦センタ
     リングは #721 2巡目で撤回）、emporium/arcanelab は自前のスクロールリスト。 */
  display: flex;
  flex-direction: column;
}
</style>
